import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { useCompanyUsers } from '../../hooks/useCompanyUsers';
import { usePermissions } from '../../hooks/usePermissions';
import {
  AGILE_TASK_STATUSES,
  DEFAULT_AGILE_STATUS,
  typeLabel,
} from '../../constants/agileTasks';
import {
  formatSprintDate,
  resolveActiveSprint,
  sprintSelectLabel,
} from '../../constants/agileSprints';
import TaskModal from './TaskModal';
import {
  DashboardModuleRoot,
  DashboardModuleToolbar,
  DashboardModuleBody,
} from '../layout/DashboardLayout';

function typeChipColor(type) {
  if (type === 'bug') return 'error';
  if (type === 'feature') return 'primary';
  return 'default';
}

function groupTasksByStatus(tasks) {
  const map = Object.fromEntries(AGILE_TASK_STATUSES.map((c) => [c.id, []]));
  tasks.forEach((task) => {
    const col = map[task.status] ? task.status : DEFAULT_AGILE_STATUS;
    map[col].push(task);
  });
  Object.keys(map).forEach((key) => {
    map[key].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  });
  return map;
}

/**
 * Company-scoped Kanban board over Firestore `tasks`.
 * Move tasks between columns via the status dropdown on each card (or TaskModal).
 * @param {{ userData: Record<string, unknown> | null }} props
 */
export default function KanbanBoard({ userData, openTaskId, onConsumedOpenTask }) {
  const companyId = userData?.companyId;
  const uid = auth.currentUser?.uid;
  const { checkPermission } = usePermissions(userData);
  const canWriteAgile = checkPermission('agile', 'write');

  const [tasks, setTasks] = useState([]);
  const [sprints, setSprints] = useState([]);
  const [selectedSprintId, setSelectedSprintId] = useState('');
  const { users: companyUsers } = useCompanyUsers(companyId);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [movingId, setMovingId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);

  useEffect(() => {
    if (!companyId) {
      setSprints([]);
      return undefined;
    }

    const sprintsQuery = query(
      collection(db, 'sprints'),
      where('companyId', '==', companyId),
      orderBy('startDate', 'desc'),
    );

    const unsubscribe = onSnapshot(
      sprintsQuery,
      (snap) => {
        setSprints(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (err) => {
        console.error('KanbanBoard sprints listener:', err);
        setError(err.message || 'Could not load sprints.');
      },
    );

    return () => unsubscribe();
  }, [companyId]);

  const activeSprint = useMemo(() => resolveActiveSprint(sprints), [sprints]);

  useEffect(() => {
    if (!selectedSprintId && activeSprint?.id) {
      setSelectedSprintId(activeSprint.id);
    }
  }, [activeSprint?.id, selectedSprintId]);

  useEffect(() => {
    if (!companyId || !selectedSprintId) {
      setTasks([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setError('');

    const q = query(
      collection(db, 'tasks'),
      where('companyId', '==', companyId),
      where('sprintId', '==', selectedSprintId),
      orderBy('updatedAt', 'desc'),
    );

    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        setTasks(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
        setError('');
      },
      (err) => {
        console.error('KanbanBoard tasks listener:', err);
        setError(err.message || 'Could not load tasks.');
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [companyId, selectedSprintId]);

  const tasksByColumn = useMemo(() => groupTasksByStatus(tasks), [tasks]);

  const assigneeName = useCallback(
    (assigneeUid) => {
      if (!assigneeUid) return null;
      const u = companyUsers.find((x) => x.uid === assigneeUid);
      return u?.displayName || u?.email || assigneeUid;
    },
    [companyUsers],
  );

  const openCreate = () => {
    setEditingTask(null);
    setModalOpen(true);
  };

  const openEdit = (task) => {
    setEditingTask(task);
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setEditingTask(null);
  };

  useEffect(() => {
    if (!openTaskId || !companyId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'tasks', openTaskId));
        if (cancelled || !snap.exists()) return;
        const t = { id: snap.id, ...snap.data() };
        if (t.sprintId) setSelectedSprintId(t.sprintId);
        setEditingTask(t);
        setModalOpen(true);
        onConsumedOpenTask?.();
      } catch (e) {
        console.warn('KanbanBoard openTaskId:', e);
        onConsumedOpenTask?.();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [openTaskId, companyId, onConsumedOpenTask]);

  const handleSave = async (payload) => {
    if (!companyId || !uid) return;
    setSaving(true);
    setError('');
    const previousTasks = tasks;
    try {
      if (editingTask?.id) {
        setTasks((prev) =>
          prev.map((t) => (t.id === editingTask.id ? { ...t, ...payload } : t)),
        );
        await updateDoc(doc(db, 'tasks', editingTask.id), {
          ...payload,
          updatedAt: serverTimestamp(),
          updatedBy: uid,
        });
      } else {
        const columnTasks = tasksByColumn[payload.status] || [];
        await addDoc(collection(db, 'tasks'), {
          companyId,
          ...payload,
          projectId: null,
          sprintId: selectedSprintId || null,
          sortOrder: columnTasks.length,
          linkedTicketIds: [],
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          createdBy: uid,
          updatedBy: uid,
        });
      }
      closeModal();
    } catch (e) {
      console.error('KanbanBoard save:', e);
      setTasks(previousTasks);
      setError(e.message || 'Could not save task.');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (taskId, nextStatus) => {
    if (!uid) return;
    const task = tasks.find((t) => t.id === taskId);
    if (!task || task.status === nextStatus) return;

    setMovingId(taskId);
    setError('');
    const previousTasks = tasks;
    const columnTasks = tasksByColumn[nextStatus] || [];
    setTasks((prev) =>
      prev.map((t) =>
        t.id === taskId ?
          { ...t, status: nextStatus, sortOrder: columnTasks.length } :
          t,
      ),
    );
    try {
      await updateDoc(doc(db, 'tasks', taskId), {
        status: nextStatus,
        sortOrder: columnTasks.length,
        updatedAt: serverTimestamp(),
        updatedBy: uid,
      });
    } catch (e) {
      console.error('KanbanBoard status change:', e);
      setTasks(previousTasks);
      setError(e.message || 'Could not move task.');
    } finally {
      setMovingId(null);
    }
  };

  const selectedSprint = sprints.find((s) => s.id === selectedSprintId);

  if (!companyId) {
    return (
      <Alert severity="info">
        You need a company assignment to use the agile board.
      </Alert>
    );
  }

  return (
    <DashboardModuleRoot>
      <DashboardModuleToolbar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} noWrap>
            Kanban
          </Typography>
          {selectedSprint && (
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {formatSprintDate(selectedSprint.startDate)} – {formatSprintDate(selectedSprint.endDate)}
              {selectedSprint.goal ? ` · ${selectedSprint.goal}` : ''}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="kanban-sprint-label">Sprint</InputLabel>
            <Select
              labelId="kanban-sprint-label"
              label="Sprint"
              value={selectedSprintId}
              onChange={(e) => setSelectedSprintId(e.target.value)}
              disabled={sprints.length === 0}
            >
              {sprints.map((sprint) => (
                <MenuItem key={sprint.id} value={sprint.id}>
                  {sprintSelectLabel(sprint.id, sprints)}
                  {' · '}
                  {formatSprintDate(sprint.startDate)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {canWriteAgile && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={openCreate}
              disabled={!selectedSprintId}
              sx={{ fontWeight: 700 }}
            >
              New task
            </Button>
          )}
        </Box>
      </DashboardModuleToolbar>

      {sprints.length === 0 && !loading && (
        <Alert severity="info" sx={{ mx: 1.5, mt: 1, flexShrink: 0 }}>
          No sprints yet. Create one in Sprint Planning and assign backlog tasks before using the board.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mx: 1.5, mt: 1, flexShrink: 0 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <CircularProgress />
        </Box>
      ) : (
        <DashboardModuleBody sx={{ px: 1.5, py: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              gap: 1.5,
              alignItems: 'flex-start',
              minHeight: '100%',
              width: 'max-content',
              minWidth: '100%',
            }}
          >
            {AGILE_TASK_STATUSES.map((column) => (
              <Paper
                key={column.id}
                variant="outlined"
                sx={{
                  flex: '0 0 280px',
                  width: 280,
                  display: 'flex',
                  flexDirection: 'column',
                  bgcolor: 'action.hover',
                  minHeight: 240,
                }}
              >
                <Box
                  sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 1.5,
                    py: 1,
                    bgcolor: 'action.hover',
                    borderBottom: 1,
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={700}>
                    {column.label}
                  </Typography>
                  <Chip size="small" label={(tasksByColumn[column.id] || []).length} />
                </Box>

                <Box sx={{ p: 1.5, pt: 1 }}>
                  {(tasksByColumn[column.id] || []).map((task) => (
                    <Paper
                      key={task.id}
                      elevation={1}
                      sx={{
                        p: 1.5,
                        mb: 1,
                        cursor: 'pointer',
                        opacity: movingId === task.id ? 0.6 : 1,
                        '&:hover': { boxShadow: 3 },
                      }}
                      onClick={() => openEdit(task)}
                    >
                    <Typography variant="subtitle2" title={task.title}>
                      {task.title}
                    </Typography>
                    {task.description && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {task.description}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1, mb: 1 }}>
                      <Chip
                        size="small"
                        label={typeLabel(task.type)}
                        color={typeChipColor(task.type)}
                        variant="outlined"
                      />
                      {task.assigneeUid && (
                        <Chip
                          size="small"
                          label={assigneeName(task.assigneeUid)}
                          variant="outlined"
                        />
                      )}
                    </Box>
                    {canWriteAgile ? (
                      <FormControl
                        size="small"
                        fullWidth
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Select
                          value={task.status || DEFAULT_AGILE_STATUS}
                          onChange={(e) => handleStatusChange(task.id, e.target.value)}
                          disabled={movingId === task.id}
                        >
                          {AGILE_TASK_STATUSES.map((s) => (
                            <MenuItem key={s.id} value={s.id}>
                              {s.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <Chip
                        size="small"
                        label={
                          AGILE_TASK_STATUSES.find((s) => s.id === (task.status || DEFAULT_AGILE_STATUS))
                            ?.label || task.status
                        }
                        variant="outlined"
                        sx={{ mt: 0.5 }}
                      />
                    )}
                  </Paper>
                ))}
              </Box>
            </Paper>
          ))}
          </Box>
        </DashboardModuleBody>
      )}

      <TaskModal
        open={modalOpen}
        onClose={closeModal}
        task={editingTask}
        companyId={companyId}
        userData={userData}
        companyUsers={companyUsers}
        saving={saving}
        onSave={handleSave}
        readOnly={!canWriteAgile}
      />
    </DashboardModuleRoot>
  );
}
